import Stack from '@mui/material/Stack';
import { type ReactNode, useState } from 'react';
import { ErrorState } from '../../../media/shared/ErrorState.js';
import type { CreativeScoreComponentName } from '../../data/creativeScore.js';
import { CREATIVE_SCORE_COMPONENT_ACCENT } from '../../data/presentation.js';
import type { CreativeDetailScope } from '../data/api.js';
import { useSingleCreativeScore } from '../data/api.js';
import { SectionSkeleton } from '../shared/SectionSkeleton.js';
import { DropOffSection } from './DropOffSection.js';
import { ScoreBreakdownCard } from './ScoreBreakdownCard.js';
import { ScoreOverTimeSection, type ScoreSeriesSelection } from './ScoreOverTimeSection.js';

interface ScoresTabProps {
  scope: CreativeDetailScope;
  creativeId: string;
  leadingKpiLabel: string;
}

export function ScoresTab({ scope, creativeId, leadingKpiLabel }: ScoresTabProps): ReactNode {
  const score = useSingleCreativeScore({ ...scope, creativeId }, true);
  const [seriesNames, setSeriesNames] = useState<ScoreSeriesSelection>(['score']);
  const toggleComponent = (name: CreativeScoreComponentName): void =>
    setSeriesNames((current) => (current.includes(name) ? ['score'] : ['score', name]));
  const selectedComponents = seriesNames.filter(
    (name): name is CreativeScoreComponentName => name in CREATIVE_SCORE_COMPONENT_ACCENT,
  );

  return (
    <Stack sx={{ gap: 5 }}>
      {score.isError ? (
        <ErrorState />
      ) : score.data === undefined ? (
        <SectionSkeleton variant="tiles" tileCount={3} />
      ) : (
        <ScoreBreakdownCard
          score={score.data.score}
          previous={score.data.previous}
          components={score.data.components}
          leadingKpiLabel={leadingKpiLabel}
          selectedComponents={selectedComponents}
          onComponentSelect={toggleComponent}
        />
      )}
      <ScoreOverTimeSection
        scope={scope}
        creativeId={creativeId}
        leadingKpiLabel={leadingKpiLabel}
        seriesNames={seriesNames}
        onSeriesNamesChange={setSeriesNames}
      />
      {score.data?.creativeType === 'VIDEO' ? (
        <DropOffSection scope={scope} creativeId={creativeId} />
      ) : null}
    </Stack>
  );
}
